import React, { useEffect, useState } from 'react';
import { useAppSelector } from '../../store';
import { auth } from '../../firebase/config';
import HeadDashboard from './HeadDashboard';
import { fetchCompanyIdAndPsoInfo } from '../request_management/request_form/service/requestManagementService';

/** Auth uid, Firestore pos doc id, and business `psoId` may all appear on branches / createdBy. */
function selfPsoScopeIds(uid: string, info: { posDocId: string; psoId: string } | null): string[] {
    const merged = new Set<string>();
    merged.add(uid);
    if (info) {
        if (info.posDocId) merged.add(String(info.posDocId));
        if (info.psoId) merged.add(String(info.psoId));
    }
    merged.delete('');
    return Array.from(merged);
}

export default function PsoDashboard() {
    const pso = useAppSelector((s) => s.user.pso);
    const uid = auth.currentUser?.uid;
    const [managedIds, setManagedIds] = useState<string[]>([]);
    const [scopeReady, setScopeReady] = useState(false);

    useEffect(() => {
        if (!uid) return;
        let cancelled = false;
        setScopeReady(false);
        void (async () => {
            try {
                const info = await fetchCompanyIdAndPsoInfo(uid);
                if (cancelled) return;
                if (info) {
                    setManagedIds(selfPsoScopeIds(uid, { posDocId: info.posDocId, psoId: info.psoId }));
                } else {
                    setManagedIds([uid]);
                }
            } catch (err) {
                console.error('PSO dashboard scope resolve failed:', err);
                if (!cancelled) setManagedIds([uid]);
            } finally {
                if (!cancelled) setScopeReady(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [uid]);

    if (!uid || !scopeReady) {
        return (
            <div className="space-y-6">
                <div className="h-8 w-64 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700" />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="h-24 animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700" />
                    ))}
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-4">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <div key={`t-${i}`} className="h-20 animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700" />
                    ))}
                </div>
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    <div className="h-56 animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700" />
                    <div className="h-56 animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700" />
                </div>
            </div>
        );
    }

    return (
        <HeadDashboard
            managedPsoAuthIds={managedIds}
            dashboardTitle="PSO dashboard"
            dashboardSubtitle={`Welcome, ${pso?.name || 'PSO'}. Your branches, surveys, and requests.`}
            hideStaffKpis={true}
        />
    );
}
